import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Selector, useDispatch, useSelector } from 'react-redux';
import { createSelector, OutputParametricSelector } from 'reselect';
import { assoc, equals, pipe } from 'ramda';
import { renameKeys } from 'ramda-adjunct';
import { Badge, Button, Menu, Tooltip, ButtonGroup } from '@material-ui/core';
import CropFree from '@material-ui/icons/CropFree';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { addError, addSuccess, addWarning, dismissType } from 'actions/Error';
import {
  addProduct,
  applyCoupon,
  updateProductOrder,
} from 'actions/ShoppingCart';
import Icon from 'components/Icon';
import { PosPlugin } from 'plugins/plugin';
import {
  getCurrencyFormatter,
  getSetting,
  getShowPricesWithQuantity,
  getShowPricesWithTax,
  getSearchCustomerCodeFromMiddle,
} from 'reducers/configs/settings';
import { getSelectedCustomer } from 'reducers/customerSearch';
import { getPluginHooksByName } from 'reducers/Plugins';
import { getLastSale } from 'reducers/sales';
import {
  getAppliedCoupons,
  getProductsInShoppingCart,
  getShoppingCartDiscountsDict,
  getTotal,
  getTotalNet,
  getTotalTax,
  getVisibleTotalDiscount,
} from 'reducers/ShoppingCart';
import { saveCustomer, setCustomer } from 'actions/customerSearch';
import { publish, useCentrifugoDisplayClient } from 'services/CentrifugoWS';
import { getCustomers } from 'services/ErplyAPI';
import { verifyCoupon } from 'services/ErplyAPI/campaigns';
import * as api from 'services/ErplyAPI/mail';
import { isEmpty, sleep } from 'utils';
import useProducts from 'utils/hooks/useProducts';
import { openModalPage, openPluginModalPage } from 'actions/modalPage';
import { modalPages as mp } from 'constants/modalPage';
import { RootState } from 'reducers';
import { Customer } from 'types/Customer';

import {
  couponDeclinedMessage,
  customerCreationRejectionMessage,
  enterCouponMessage,
  finishedSaleCentrifugoMessage,
  getDonationMessage,
  getTipMessage,
  hideMiscContentMessage,
  posConnectedCentrifugoMessage,
  startNewSaleCentrifugoMessage,
  updateCustomerDeclinedMessage,
} from './constants';
import {
  openWindowOnSelectedScreen,
  setNeedsStartNew,
  setProcessingCustomer,
  resetCustomerFormState,
} from './rdx/actions';
import ScreenSelectMenu from './screenSelect/ScreenSelectMenu';
import useScreenDetector from './screenSelect/useScreenDetector';
import {
  Configuration,
  CustomerDisplayState,
  QrCode,
  GetTipResponse,
  UpdateCustomerData,
} from './types';
import { handleSelectedUpsellProducts } from './features/upsellProducts';
import { SearchCustomer, ActionButton } from './components';
import { ACTIONS } from './constants/actions';
import {
  getChannelName,
  getCustomerCreationMessage,
  getCustomQRCodeMessage,
  getDiscountAsPercentage,
  getDonationProductId,
  getOpenCustomerFormOnCreate,
  getQRCodeConfig,
  getSetConfigCentrifugoMessage,
  getSetPromotionsMessage,
  getSetSmallPromotionsMessage,
  getShouldShowOpenCustomerDisplayButton,
  getTipProductPrice,
  getDynamicTipProduct,
  getTipProductId,
  getNeedsStartNew,
  getNeedToCloseCustomerFormOnDisplay,
  getProcessingCustomer,
} from './rdx/reducer';
import {
  handleTipProductApproval,
  handleTipProductDecline,
} from './features/tipProduct';

const tipInputInitValue = { percentage: 0 };

const getDefaultCustomerDisplayData = createSelector(
  state => getShowPricesWithTax(state),
  state => getProductsInShoppingCart(state),
  state => getVisibleTotalDiscount(state),
  state => getTotalTax(state),
  state => getTotalNet(state),
  state => getTotal(state),
  state => getCurrencyFormatter(state),
  state => getSelectedCustomer(state),
  state => getShoppingCartDiscountsDict(state),
  state => getSetting('pos_cart_rows_to_bottom')(state),
  (
    isWithTax,
    shoppingCart,
    totalDiscount,
    totalVat,
    totalNet,
    total,
    formatCurrency,
    activeCustomer,
    shoppingCartDiscounts,
    rowsToBottom,
  ) => {
    const cart = rowsToBottom
      ? shoppingCart
      : Array.from(shoppingCart).reverse();
    return {
      products: cart.map(o => ({
        id: o.orderIndex,
        name: o.name,
        code: o.code,
        qty: o.amount,
        basePrice: isWithTax
          ? formatCurrency(Number(o.priceListPriceWithVAT))
          : formatCurrency(Number(o.priceListPrice)),
        disc: (shoppingCartDiscounts[o.rowNumber] || [])
          ?.map(a => `${a.affected}x -${a.percentage}%`)
          .join('\n'),
        final: isWithTax
          ? formatCurrency(Number(o.rowTotal))
          : formatCurrency(Number(o.rowNetTotal)),
      })),
      surcharges: [
        { name: 'Tax', id: 'tax', amount: formatCurrency(totalVat) },
      ],
      discount: formatCurrency(totalDiscount, { roundDown: true }),
      net: formatCurrency(totalNet),
      surcharge: formatCurrency(totalVat),
      total: formatCurrency(total),
      customer: {
        fullName: activeCustomer?.fullName?.replace(/,/g, ''),
        firstName: activeCustomer?.firstName,
        lastName: activeCustomer?.lastName,
        email: activeCustomer?.email,
        birthday: activeCustomer?.birthday,
      },
    };
  },
);

const getCustomerDisplayData = createSelector(
  state =>
    getPluginHooksByName<Record<string, any>>('customHooks')(state)
      .map(h => h.customerDisplayGetData)
      .filter(a => a) as OutputParametricSelector<
      any,
      CustomerDisplayState,
      CustomerDisplayState,
      any
    >[],
  state => state,
  (hooks, rootState) => {
    const result = ([getDefaultCustomerDisplayData, ...hooks] as any[]).reduce(
      (prev, selector) => selector(rootState, prev),
      undefined,
    ) as CustomerDisplayState;
    return result;
  },
);

const CustomerDisplayConnection = ({
  channelName,
}: {
  channelName: string;
}) => {
  const channel = `customer_display:${channelName}`;
  const deps: any[] = [];
  const useDepSelector = <A,>(sel: Selector<any, A>): A => {
    const val = useSelector<any, A>(sel);
    deps.push(val);
    return val;
  };
  deps.push(channel);

  const data = useDepSelector(getCustomerDisplayData);

  const [memoized, setMemoized] = useState(data);

  useEffect(() => {
    if (!equals(memoized, data)) setMemoized(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (channel) {
      publish({ message: posConnectedCentrifugoMessage, channel });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    if (channel) {
      publish({
        message: {
          action: 'SET_PRODUCTS_CD',
          data: {
            ...memoized,
          },
          timestamp: Date.now(),
        },
        channel,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoized, channel]);
  return null;
};

type ActionType =
  | 'REGISTER_CUSTOMER_DATA'
  | 'CUSTOMER_EMAIL_INPUT'
  | 'CUSTOMER_PHONE_INPUT'
  | 'CUSTOMER_CODE_INPUT'
  | 'ENTERED_COUPON_CODE'
  | 'UPDATE_CUSTOMER_DATA'
  | 'ADD_TIP'
  | 'ADD_DONATION';

type InitLatestMessage = { data: null; type: '' };
type LatestMessage =
  | InitLatestMessage
  | {
      data: string | { [key: string]: any };
      type: ActionType;
    };

export const ComponentHeader: PosPlugin<Configuration>['ComponentHeader'] = ({
  children,
}) => {
  const { t } = useTranslation('customerDisplay');
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const tipSum = useSelector(getTipProductPrice);
  const tipOrderIndex = useSelector(getDynamicTipProduct)?.orderIndex;
  const tipProductId = useSelector(getTipProductId);
  const channelName = useSelector(getChannelName);
  const channel = `customer_display:${channelName}`;
  const format = useSelector(getCurrencyFormatter);
  const isWithTax = useSelector(getShowPricesWithTax);
  const isWithQty = useSelector(getShowPricesWithQuantity);
  const reverse = !useSelector(getSetting('pos_cart_rows_to_bottom'));
  const productsInCart = useSelector(getProductsInShoppingCart);
  const cart = useMemo(
    () => (reverse ? Array.from(productsInCart).reverse() : productsInCart),
    [reverse, productsInCart],
  );

  const visibleTotalDiscount = useSelector(getVisibleTotalDiscount);
  const total = useSelector(getTotal);
  const net = useSelector(getTotalNet);
  const tax = useSelector(getTotalTax);
  const needsStartNew = useSelector(getNeedsStartNew);
  const needToCloseCustomerFormOnDisplay = useSelector(
    getNeedToCloseCustomerFormOnDisplay,
  );
  const customerProcessingState = useSelector(getProcessingCustomer);

  const handleFormClosure = useCallback(() => {
    if (customerProcessingState) {
      if (customerProcessingState === 'update') {
        publish({
          channel: `customer_display:${channelName}`,
          message: updateCustomerDeclinedMessage,
        });
      } else {
        publish({
          channel: `customer_display:${channelName}`,
          message: customerCreationRejectionMessage,
        });
      }
      dispatch(resetCustomerFormState());
    }
  }, [channelName, customerProcessingState, dispatch]);

  useEffect(() => {
    if (needToCloseCustomerFormOnDisplay) {
      handleFormClosure();
    }
  }, [needToCloseCustomerFormOnDisplay, handleFormClosure]);

  const combo = useMemo(
    () => ({
      cart,
      summary: {
        disc: format(visibleTotalDiscount, { roundDown: true }),
        total: format(total),
        net: format(net),
        tax: format(tax),
      },
    }),
    [cart, format, visibleTotalDiscount, total, net, tax],
  );

  const [memoized, setMemoized] = useState(combo);
  const [registerCustomerData, setRegisterCustomerData] = useState({});
  const [updateCustomerData, setUpdateCustomerData] = useState<
    UpdateCustomerData['customer'] & {
      customerId?: UpdateCustomerData['customerId'];
    }
  >({});
  const [customerEmailInput, setCustomerEmailInput] = useState('');
  const [customerPhoneInput, setCustomerPhoneInput] = useState('');
  const [customerCodeInput, setCustomerCodeInput] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [donationInput, setDonationInput] = useState(0);
  const [tipInput, setTipInput] = useState<GetTipResponse['tip']>(
    tipInputInitValue,
  );
  const hasTipPercentage = 'percentage' in tipInput && tipInput.percentage > 0;
  const hasTipAmount = 'amount' in tipInput && tipInput.amount > 0;
  const [emailInputAction, setEmailInputAction] = useState('');
  const [latestMessage, setLatestMessage] = useState<LatestMessage>({
    data: null,
    type: '',
  });

  const discountAsPercentage = useSelector(getDiscountAsPercentage);
  const openCustomerFormOnCreate = useSelector(getOpenCustomerFormOnCreate);
  const { invoiceID } = useSelector(getLastSale);

  const customerCreationMessage = useSelector(getCustomerCreationMessage);
  const setConfigMessage = useSelector(getSetConfigCentrifugoMessage);
  const promoMsg = useSelector(getSetPromotionsMessage);
  const smallPromoMsg = useSelector(getSetSmallPromotionsMessage);
  const qrCodeConfig = useSelector(getQRCodeConfig);
  const qrCodeMessage = useSelector(getCustomQRCodeMessage);
  const shouldShowOpenCustomerDisplayButton = useSelector(
    getShouldShowOpenCustomerDisplayButton,
  );

  const coupons = useSelector(getAppliedCoupons);
  const couponDeniedMessage = (reason: string) => {
    return {
      action: 'COUPON_CODE_FAILED',
      reason,
      showAgain: false,
    };
  };
  const couponAppliedMessage = {
    action: 'COUPON_CODE_APPLIED',
    discount: '',
    summary: memoized.summary,
    showAgain: false,
  };
  const donationProductId = useSelector(getDonationProductId);
  const shouldSearchFromMiddle = useSelector(getSearchCustomerCodeFromMiddle);
  const hasDynamicTipProductInCart = !!useSelector(getDynamicTipProduct);

  const {
    products: [donationProduct],
  } = useProducts({ productID: donationProductId });

  const saveCustomerThruButton = async info => {
    const newCustomerData = assoc(
      'addresses',
      [{ postalCode: info.zip }],
      info,
    );
    if (openCustomerFormOnCreate) {
      dispatch(setProcessingCustomer('register'));
      dispatch(
        openModalPage({
          component: mp.createCustomer,
          groupID: mp.createCustomer,
          props: {
            newCustomer: true,
            newCustomerData,
          },
        }),
      );
      return;
    }

    let customerSaved = false;
    await dispatch(
      saveCustomer(newCustomerData, () => {
        customerSaved = true;
      }),
    );
    if (!customerSaved) {
      publish({
        channel: `customer_display:${channelName}`,
        message: customerCreationRejectionMessage,
      });
    }
  };

  const updateCustomerThruButton = async (
    info: UpdateCustomerData['customer'],
    customerID?: UpdateCustomerData['customerId'],
  ) => {
    if (!customerID) {
      dispatch(addError('Cannot update customer without ID'));
      return;
    }
    const customerToBeUpdated = await getCustomers({
      customerID,
    }).then(([customer]) => customer);

    const newCustomerData: Customer = {
      ...(customerToBeUpdated ?? {}),
      ...info,
    };

    if (info.zip) {
      const firstAddress = newCustomerData.addresses?.[0];
      if (firstAddress) {
        newCustomerData.addresses[0].postalCode = String(info.zip);
      } else {
        newCustomerData.addresses = [
          { postalCode: String(info.zip) },
        ] as Customer['addresses'];
      }
    }
    if (info.emailOptOut !== undefined) {
      newCustomerData.emailOptOut = info.emailOptOut ? 1 : 0;
    }

    if (openCustomerFormOnCreate) {
      dispatch(setProcessingCustomer('update'));
      dispatch(
        openModalPage({
          component: mp.createCustomer,
          groupID: mp.createCustomer,
          props: {
            customer: newCustomerData,
          },
        }),
      );
      return;
    }

    let customerSaved = false;
    await dispatch(
      saveCustomer(newCustomerData, () => {
        customerSaved = true;
      }),
    );
    if (!customerSaved) {
      publish({
        channel: `customer_display:${channelName}`,
        message: {
          action: ACTIONS.CUSTOMER.UPDATE_CUSTOMER_FAILED,
          reason: 'Failed to update the customer',
          showAgain: true,
        },
      });
    }
  };

  const parseDonationToPOS = amount => {
    dispatch(
      addProduct({
        productID: donationProductId,
        price: amount,
        amount: 1,
      }),
    );
    publish({
      channel,
      message: {
        action: 'ADDED_DONATION',
        onlyDonationData: true,
        product: {
          ...donationProduct,
          price: amount,
        },
        summary: memoized.summary,
      },
    });
  };

  const searchCustomerThruButton = async ({ info, field }) => {
    const customers = await getCustomers({
      searchFromMiddle: shouldSearchFromMiddle ? 1 : 0,
      [field]: info,
      doNotCalculateRecordsTotal: 1,
    });
    if (customers.length === 1) {
      const customer = customers[0];
      dispatch(
        addSuccess(
          t('customerSearch.selected', {
            lastName: customer?.lastName,
            firstName: customer?.firstName,
          }),
          {
            dismissible: true,
            selfDismiss: true,
          },
        ),
      );
      dispatch(setCustomer({ data: customer }));
    } else if (customers.length > 0) {
      dispatch(
        openPluginModalPage('DuplicateCustomersSelectionForm')({
          isPopup: true,
          props: {
            customers,
          },
        }),
      );
    } else {
      dispatch(
        addWarning(t('customerSearch.noResults'), {
          dismissible: true,
          selfDismiss: true,
        }),
      );
    }
  };

  const sendEmailToCustomer = async ({ email }) => {
    try {
      dispatch(
        addWarning(i18next.t('payment:confirmView.alerts.sendingEmail'), {
          errorType: 'Receipt email',
          dismissible: false,
          selfDismiss: false,
        }),
      );
      await api.sendSalesDocumentByMail({
        invoiceID,
        email,
      });
      dispatch(dismissType('Receipt email'));
      dispatch(
        addSuccess(
          i18next.t('payment:confirmView.alerts.sendingEmail', {
            context: 'done',
          }),
          {
            errorType: 'Receipt email',
            selfDismiss: true,
          },
        ),
      );
      // after email sent,
      publish({
        channel,
        message: finishedSaleCentrifugoMessage,
      });
      dispatch(setNeedsStartNew(true));
    } catch (err) {
      dispatch(dismissType('Receipt email'));
      dispatch(
        addWarning(err.message, {
          dismissible: false,
          selfDismiss: true,
          errorType: 'Receipt email',
        }),
      );
    }
  };

  const searchAddCouponThruButton = id => {
    if (coupons.find(coup => coup.uniqueIdentifier === id)) {
      dispatch(
        addWarning(t('alerts:promotions.couponAlreadyApplied'), {
          selfDismiss: true,
        }),
      );
      publish({
        channel,
        message: couponDeniedMessage(
          t('alerts:promotions.couponAlreadyApplied'),
        ),
      });
      return;
    }
    verifyCoupon(id)
      .then(res => res[0])
      .then(coupon => {
        dispatch(applyCoupon(coupon));
      })
      .then(() => {
        dispatch(
          addSuccess(t('alerts:promotions.couponSuccessfullyApplied'), {
            selfDismiss: true,
          }),
        );
        publish({
          channel,
          message: couponAppliedMessage,
        });
      })
      .catch(err => {
        dispatch(addError(err.message, { selfDismiss: true }));
        publish({
          channel,
          message: couponDeniedMessage(err.message),
        });
      });
  };

  const actions: {
    [key in ActionType]: {
      accept: () => void;
      decline?: () => void;
    };
  } = {
    REGISTER_CUSTOMER_DATA: {
      accept: async () => {
        saveCustomerThruButton(registerCustomerData);
        setRegisterCustomerData({});
        setLatestMessage({ data: null, type: '' });
      },
      decline: async () => {
        publish({
          channel,
          message: customerCreationRejectionMessage,
        });
        setRegisterCustomerData({});
        setLatestMessage({ data: null, type: '' });
      },
    },
    CUSTOMER_EMAIL_INPUT: {
      accept: async () => {
        searchCustomerThruButton({
          info: customerEmailInput,
          field: 'searchNameIncrementally',
        });
        setCustomerEmailInput('');
        setLatestMessage({ data: null, type: '' });
      },
    },
    UPDATE_CUSTOMER_DATA: {
      accept: async () => {
        updateCustomerThruButton(
          updateCustomerData,
          updateCustomerData.customerId,
        );
        setUpdateCustomerData({});
        setLatestMessage({ data: null, type: '' });
      },
      decline: async () => {
        publish({
          channel,
          message: updateCustomerDeclinedMessage,
        });
        setUpdateCustomerData({});
        setLatestMessage({ data: null, type: '' });
      },
    },
    CUSTOMER_PHONE_INPUT: {
      accept: async () => {
        searchCustomerThruButton({
          info: customerPhoneInput,
          field: 'searchNameIncrementally',
        });
        setCustomerPhoneInput('');
        setLatestMessage({ data: null, type: '' });
      },
    },
    CUSTOMER_CODE_INPUT: {
      accept: () => {
        searchCustomerThruButton({
          info: customerCodeInput,
          field: 'searchRegistryCode',
        });
        setCustomerCodeInput('');
        setLatestMessage({ data: null, type: '' });
      },
    },
    ENTERED_COUPON_CODE: {
      accept: async () => {
        searchAddCouponThruButton(couponInput);
        setCouponInput('');
        setLatestMessage({ data: null, type: '' });
      },
      decline: async () => {
        publish({
          channel,
          message: couponDeclinedMessage,
        });
        setCouponInput('');
        setLatestMessage({ data: null, type: '' });
      },
    },
    ADD_DONATION: {
      accept: async () => {
        parseDonationToPOS(donationInput);
        setDonationInput(0);
        setLatestMessage({ data: null, type: '' });
      },
    },
    ADD_TIP: {
      accept: async () => {
        if (!tipProductId) {
          dispatch(
            addWarning('Cannot accept tip since tip product is not configured'),
          );
        } else {
          await dispatch(handleTipProductApproval(tipInput));
          setTipInput(tipInputInitValue);
          setLatestMessage({ data: null, type: '' });
        }
      },
      decline: async () => {
        await dispatch(handleTipProductDecline());
        setTipInput(tipInputInitValue);
        setLatestMessage({ data: null, type: '' });
      },
    },
  };

  const shouldShowDeclineButton = (type: ActionType) => {
    return !!actions[type].decline;
  };

  const discountAmountByTax = isWithTax
    ? 'totalDiscountAmountWithVAT'
    : 'totalDiscountAmount';

  useEffect(() => {
    if (!equals(memoized, combo)) {
      setMemoized(combo);
    }
  }, [combo, memoized]);

  useEffect(() => {
    if (tipOrderIndex) {
      dispatch(
        updateProductOrder({ orderIndex: tipOrderIndex, basePrice: tipSum }),
      );
    }
  }, [tipSum, tipOrderIndex, dispatch]);

  const setProductMessage = useMemo(() => {
    const convertPriceForDisplay = cartRow => {
      const basePrice = isWithTax
        ? cartRow.finalPriceWithVAT
        : cartRow.finalPrice;
      const multiplier = isWithQty ? cartRow.qty : 1;
      const price = basePrice * multiplier;
      return price;
    };
    return {
      action: 'SET_PRODUCTS',
      products: memoized.cart.map(
        pipe(
          renameKeys({
            amount: 'qty',
          }),
          prod =>
            assoc(
              'disc',
              // eslint-disable-next-line no-nested-ternary
              discountAsPercentage
                ? Number(prod?.totalDiscount) > 0
                  ? `${Number(prod?.totalDiscount)?.toFixed(2)}%`
                  : ''
                : prod?.[discountAmountByTax] > 0
                ? format(
                    isWithQty
                      ? prod?.[discountAmountByTax] * Number(prod?.qty)
                      : prod?.[discountAmountByTax],
                  )
                : '',
            )(prod),
          prod =>
            assoc('price', format(convertPriceForDisplay(prod) ?? 0))(prod),
        ),
      ),
      summary: memoized.summary,
    };
  }, [
    memoized.cart,
    memoized.summary,
    isWithTax,
    isWithQty,
    discountAsPercentage,
    discountAmountByTax,
    format,
  ]);

  const [qrCodeState, setQrCodeState] = useState<QrCode | null>(null);
  const hasCart = setProductMessage.products.length !== 0;

  const newSaleWhileAfterSaleQrIsShown = qrCodeState?.isAfterSaleQR && hasCart;

  useEffect(() => {
    if (!qrCodeState) return () => undefined;
    if (newSaleWhileAfterSaleQrIsShown) {
      setQrCodeState(null);
      publish({
        channel,
        message: hideMiscContentMessage,
      });
      return () => undefined;
    }
    if (!newSaleWhileAfterSaleQrIsShown) {
      const i = setTimeout(() => {
        setQrCodeState(null);
        publish({
          channel,
          message: hideMiscContentMessage,
        });
        if (qrCodeState.isAfterSaleQR) {
          publish({
            channel,
            message: finishedSaleCentrifugoMessage,
          });
        }
      }, qrCodeConfig.qrTimeout * 1000);
      return () => clearTimeout(i);
    }
    return () => undefined;
  }, [
    channel,
    newSaleWhileAfterSaleQrIsShown,
    qrCodeConfig.qrTimeout,
    qrCodeState,
  ]);

  const { postMessage } = useCentrifugoDisplayClient({
    channel,
    listener: msg => {
      const customerDisplayInput = JSON.parse(msg.data.data);
      const { action } = customerDisplayInput;
      if (action === ACTIONS.CONFIG.GET_CONFIG) {
        publish({ channel, message: setConfigMessage });
        publish({ channel, message: promoMsg });
        dispatch(setNeedsStartNew(true));
      }
      if (action === ACTIONS.MISC.ADD_TIP) {
        const { tip, action: type } = customerDisplayInput as GetTipResponse;

        if ('percentage' in tip) {
          setTipInput({ percentage: tip.percentage });
          setLatestMessage({ data: { percentage: tip.percentage }, type });
        }
        if ('amount' in tip) {
          setTipInput({ amount: tip.amount });
          setLatestMessage({ data: { amount: tip.amount }, type });
        }
      }
      if (action === ACTIONS.CUSTOMER.GET_CUSTOMER_EMAIL) {
        setEmailInputAction('search');
      }
      if (action === ACTIONS.PURCHASE.COMPLETE_PURCHASE) {
        setEmailInputAction('sale');
        dispatch(setNeedsStartNew(true));
      }
      if (action === ACTIONS.CUSTOMER.REGISTER_CUSTOMER_DATA) {
        const customerData = {
          ...customerDisplayInput.customer,
          customerType: 'PERSON',
        };
        setRegisterCustomerData(customerData);
        setLatestMessage({ data: customerData, type: action });
      }
      if (action === ACTIONS.CUSTOMER.CUSTOMER_EMAIL_INPUT) {
        const { email } = customerDisplayInput;
        if (emailInputAction === 'sale') {
          sendEmailToCustomer({ email });
        } else {
          setCustomerEmailInput(email);
          setLatestMessage({ data: email, type: action });
        }
      }
      if (action === ACTIONS.CUSTOMER.CUSTOMER_CODE_INPUT) {
        const { code } = customerDisplayInput;
        setCustomerCodeInput(code);
        setLatestMessage({ data: code, type: action });
      }
      if (action === ACTIONS.CUSTOMER.CUSTOMER_PHONE_INPUT) {
        const { phoneNumber } = customerDisplayInput;
        setCustomerPhoneInput(phoneNumber);
        setLatestMessage({ data: phoneNumber, type: action });
      }
      // Either closed from CD or successfully saved from POS
      if (
        [
          ACTIONS.CUSTOMER.UPDATE_CUSTOMER_CLOSED,
          ACTIONS.CUSTOMER.UPDATE_CUSTOMER_SUCCESSFUL,
          ACTIONS.CUSTOMER.REGISTER_CUSTOMER_SUCCESS,
          ACTIONS.CUSTOMER.REGISTER_CUSTOMER_CLOSED,
        ].includes(action)
      ) {
        dispatch(setProcessingCustomer(null));
      }
      if (action === ACTIONS.CUSTOMER.UPDATE_CUSTOMER_DATA) {
        const {
          customer,
          customerId,
        } = customerDisplayInput as UpdateCustomerData;
        setUpdateCustomerData({ ...customer, customerId });
        setLatestMessage({ data: customer, type: action });
      }
      if (action === ACTIONS.SALE.START_NEW) {
        dispatch(setNeedsStartNew(false));
      }
      if (action === ACTIONS.COUPON.ENTERED_COUPON_CODE) {
        const { couponCode } = customerDisplayInput;
        setCouponInput(couponCode);
        setLatestMessage({ data: couponCode, type: action });
      }
      if (
        action === ACTIONS.PURCHASE.PURCHASE_INACTIVITY_TIMEOUT ||
        action === ACTIONS.SALE.FINISHED
      ) {
        dispatch(setNeedsStartNew(true));
      }
      if (action === ACTIONS.MISC.ADD_DONATION) {
        const { donation } = customerDisplayInput;
        const { amount } = donation;
        setDonationInput(amount);
        setLatestMessage({ data: amount, type: action });
      }
      if (action === ACTIONS.MISC.SHOW_QR) {
        setQrCodeState(customerDisplayInput);
      }
      if (action === ACTIONS.PURCHASE.PURCHASE_UPSELL_PRODUCTS) {
        dispatch(
          handleSelectedUpsellProducts(customerDisplayInput.products ?? []),
        );
      }
    },
  });

  function getTipButtonTitleContext() {
    let context = '';
    if (!tipProductId) {
      context = 'notConfigured';
    } else if (hasDynamicTipProductInCart) {
      context = 'alreadyPresent';
    } else if (hasTipAmount || hasTipPercentage) {
      context = 'add';
    }
    return context;
  }
  const tipButtonTitleContext = getTipButtonTitleContext();

  const startNewSaleIfNeeded = useCallback(() => {
    if (!needsStartNew) return;
    postMessage(startNewSaleCentrifugoMessage);
    dispatch(setNeedsStartNew(false));
  }, [needsStartNew, postMessage, dispatch]);

  const registerCustomer = useCallback(() => {
    startNewSaleIfNeeded();
    postMessage(customerCreationMessage);
  }, [startNewSaleIfNeeded, customerCreationMessage, postMessage]);

  const enterCoupon = useCallback(() => {
    startNewSaleIfNeeded();
    postMessage(enterCouponMessage);
  }, [startNewSaleIfNeeded, postMessage]);

  const promptDonation = useCallback(() => {
    startNewSaleIfNeeded();
    postMessage(getDonationMessage);
  }, [startNewSaleIfNeeded, postMessage]);

  const promptTip = useCallback(() => {
    if (tipButtonTitleContext.length > 0 && tipButtonTitleContext !== 'add') {
      dispatch(
        addWarning(
          t('headerIcon.promptTip', { context: tipButtonTitleContext }),
        ),
      );
      return;
    }
    if (tipButtonTitleContext === 'add') {
      actions.ADD_TIP.accept();
      return;
    }
    startNewSaleIfNeeded();
    postMessage(getTipMessage);
  }, [
    tipButtonTitleContext,
    startNewSaleIfNeeded,
    postMessage,
    dispatch,
    t,
    actions.ADD_TIP,
  ]);

  const promptQRcode = useCallback(() => {
    startNewSaleIfNeeded();
    postMessage(qrCodeMessage);
    setQrCodeState(qrCodeMessage);
    sleep(qrCodeConfig.qrTimeout).then(() => {
      postMessage(hideMiscContentMessage);
    });
  }, [
    startNewSaleIfNeeded,
    postMessage,
    qrCodeMessage,
    qrCodeConfig.qrTimeout,
  ]);

  const needToHandleNewSale =
    needsStartNew && setProductMessage.products.length !== 0;

  useEffect(() => {
    const handleNewSale = () => {
      Promise.resolve(postMessage(startNewSaleCentrifugoMessage)).then(() => {
        dispatch(setNeedsStartNew(false));
      });
    };
    if (needToHandleNewSale) {
      handleNewSale();
    } else {
      postMessage(setProductMessage);
      postMessage(smallPromoMsg);
    }
  }, [
    setProductMessage,
    needToHandleNewSale,
    smallPromoMsg,
    postMessage,
    dispatch,
  ]);

  const notificationCount = [
    !!customerEmailInput.length,
    !!customerCodeInput.length,
    !!customerPhoneInput.length,
    !!couponInput.length,
    !isEmpty(registerCustomerData),
    !isEmpty(updateCustomerData),
    donationInput > 0,
    hasTipAmount || hasTipPercentage,
  ].filter(a => a).length;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useScreenDetector();

  const openCustomerDisplay = async () => {
    const { origin, pathname } = window.location;
    const relativePath = origin.concat(pathname).replace('index.html', '');

    const w = await dispatch(
      openWindowOnSelectedScreen(`${relativePath}customerDisplay/index.html`),
    );
    await sleep(1);
    w?.postMessage(
      { payload: { channelName }, type: 'setConfig' },
      `${relativePath}customerDisplay/index.html`,
    );
  };

  const openExternalCustomerDisplay = async () => {
    await dispatch(
      openWindowOnSelectedScreen('https://customer-display.erply.com/#/'),
    );
  };

  const getLatestMessageData = (): string => {
    if (!latestMessage.data) {
      return '';
    }
    if (typeof latestMessage.data === 'string') {
      return latestMessage.data;
    }
    if (latestMessage.type === ACTIONS.MISC.ADD_TIP) {
      const { data } = latestMessage;
      if ('percentage' in data) {
        return `${data.percentage}%`;
      }
      return `${data.amount}`;
    }
    return JSON.stringify(latestMessage.data);
  };

  const textStyle: React.CSSProperties = {
    fontSize: '14px',
    overflow: 'hidden',
  };

  return (
    <>
      {channelName ? (
        <CustomerDisplayConnection channelName={channelName} />
      ) : null}

      <span
        style={{ cursor: 'pointer' }}
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <Badge badgeContent={notificationCount} color="primary">
          <Icon
            name="icon_desktop"
            style={{ color: channelName ? 'lightgreen' : 'salmon' }}
            title={
              channelName
                ? 'Customer Display plugin: OK, Acting as sender'
                : 'Customer Display plugin: Channel not configured'
            }
          />
        </Badge>
      </span>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: {
            width: '275px',
            padding: '0.75rem',
          },
        }}
        MenuListProps={{
          style: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          },
        }}
      >
        {shouldShowOpenCustomerDisplayButton ? (
          <Button
            // So that matches "ActionButton" height
            style={{ minHeight: 65 }}
            variant="contained"
            color="primary"
            fullWidth
            onClick={channelName ? openCustomerDisplay : undefined}
          >
            {t('headerIcon.openDisplayApp')}
          </Button>
        ) : null}

        <Button
          onClick={channelName ? openExternalCustomerDisplay : undefined}
          variant="contained"
          color="primary"
          fullWidth
        >
          {t('headerIcon.openExtDisplayApp')}
        </Button>
        <ActionButton
          onClick={
            isEmpty(registerCustomerData)
              ? registerCustomer
              : () => actions.REGISTER_CUSTOMER_DATA.accept()
          }
          color={isEmpty(registerCustomerData) ? 'primary' : 'secondary'}
          variant="contained"
          fullWidth
          startIcon={
            <span style={textStyle}>
              {t(
                isEmpty(registerCustomerData)
                  ? 'headerIcon.sendCustomerForm'
                  : 'headerIcon.registerCustomerWithInfo',
              )}
            </span>
          }
        >
          <span className="icon_profile" />
          <span className="icon_plus" />
        </ActionButton>
        <SearchCustomer
          textStyle={textStyle}
          startNewSaleIfNeeded={startNewSaleIfNeeded}
        />
        <ActionButton
          onClick={
            couponInput.length
              ? () => actions.ENTERED_COUPON_CODE.accept()
              : enterCoupon
          }
          color={couponInput.length ? 'secondary' : 'primary'}
          variant="contained"
          fullWidth
          startIcon={
            <span style={textStyle}>
              {t(
                couponInput.length
                  ? 'headerIcon.acceptCoupon'
                  : 'headerIcon.sendCouponField',
              )}
            </span>
          }
        >
          <span className="icon_document" />
        </ActionButton>
        {donationProductId !== 0 && (
          <ActionButton
            onClick={
              donationInput === 0
                ? promptDonation
                : () => actions.ADD_DONATION.accept()
            }
            color={donationInput === 0 ? 'primary' : 'secondary'}
            variant="contained"
            fullWidth
            startIcon={
              <span style={textStyle}>
                {t(
                  donationInput === 0
                    ? 'headerIcon.promptDonation'
                    : 'headerIcon.addDonation',
                )}
              </span>
            }
          >
            <span className="icon_gift" />
          </ActionButton>
        )}

        <ActionButton
          onClick={promptQRcode}
          color="primary"
          variant="contained"
          fullWidth
          startIcon={<span style={textStyle}>{t('headerIcon.promptQR')}</span>}
          endIcon={<CropFree style={{ width: '40px', height: '40px' }} />}
        />

        {tipButtonTitleContext !== 'notConfigured' && (
          <ActionButton
            onClick={() =>
              tipButtonTitleContext === 'add'
                ? actions.ADD_TIP.accept()
                : promptTip()
            }
            color={hasTipAmount || hasTipPercentage ? 'secondary' : 'primary'}
            variant="contained"
            fullWidth
            startIcon={
              <span style={textStyle}>
                {t('headerIcon.promptTip', {
                  context: tipButtonTitleContext,
                })}
              </span>
            }
            endIcon={
              <MonetizationOnIcon style={{ width: '40px', height: '40px' }} />
            }
          />
        )}
      </Menu>
      <ScreenSelectMenu />
      {latestMessage.data ? (
        <ButtonGroup style={{ marginLeft: '1.5em' }}>
          <Tooltip
            title={
              t(`latestMessageTitles.${latestMessage.type}`, {
                data: getLatestMessageData(),
              }) as string
            }
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={() => actions[latestMessage.type].accept()}
            >
              {t(`latestMessageTitles.accept`)}
            </Button>
          </Tooltip>

          {shouldShowDeclineButton(latestMessage.type) ? (
            <Tooltip
              title={
                t(`latestMessageTitles.${latestMessage.type}_decline`) as string
              }
            >
              <Button
                variant="contained"
                onClick={() => actions[latestMessage.type].decline?.()}
              >
                {t(`latestMessageTitles.decline`)}
              </Button>
            </Tooltip>
          ) : null}
        </ButtonGroup>
      ) : null}

      {children}
    </>
  );
};
